<template>
  <section class="map">
    <div class="content">
      <div style="position: relative">
        <div :class=" { mapContainer: !isLoaded }">
          <ClientOnly>
            <div id="mapDisplay" />
            <MapColorScale
              class="mapLegend"
              :color-scale="colorScale"
            />
          </ClientOnly>
        </div>
      </div>
      <div class="inner">
        <MapSlider
          v-model="propsIndexSlider"
          :max-anlsis-index="maxAnlsisIndex"
          :min="minHour"
          :max="maxHour"
          :array="scaleList"
          :now="now"
        />
      </div>
    </div>
  </section>
</template>

<script setup>
import dayjs from 'dayjs'
import throttle from 'lodash/throttle'

import maplibregl from 'maplibre-gl'
import { Protocol } from 'pmtiles'
import { mapStyle } from '@/components/map/style.js'
import 'maplibre-gl/dist/maplibre-gl.css'

const { getSmartPlatform } = await import('@weathernews/btos-javascript-modules/src/wniutil/common.js')

const domain = 'https://wxtech-tile.wni.co.jp'
const forecastUrl = `${domain}/tile/raster/global/weather/prec/latest.json`
const anlsisUrl = `${domain}/tile/v3/data/global/weather/radar`
const minHour = -24
const maxHour = 48
const numBeforeReadHour = 3
const layerOpacity = 0.8

const props = defineProps({
  now: {
    type: Object,
    required: true
  },
  lat: {
    type: Number,
    default: 35.67
  },
  lon: {
    type: Number,
    default: 139.77
  },
  isLoc: {
    type: Boolean,
    default: false
  },
  zoom: {
    type: Number,
    default: 3.5
  }
})

const map = ref(null)
const isLoaded = ref(false)
const mouseEntered = ref(false)
const mouseEnteredTime = ref(0)

const scaleList = ref([])
const tiles = ref([])
const colorScale = ref(null)

const indexSlider = ref(0)
const propsIndexSlider = ref(0)
const maxAnlsisIndex = ref(0)

const initMap = async () => {
  const protocol = new Protocol()
  maplibregl.addProtocol('pmtiles', protocol.tile)

  map.value = new maplibregl.Map({
    container: 'mapDisplay',
    style: mapStyle,
    localIdeographFontFamily: '"Noto Sans", "Noto Sans CJK SC", sans-serif',
    center: [props.lon, props.lat],
    zoom: props.zoom,
    minZoom: 0.5,
    maxZoom: 12,
    transition: {
      duration: 0,
      delay: 0
    },
    attributionControl: false,
    scrollZoom: false,
    dragPan: getSmartPlatform() === '',
    touchPitch: false,
    dragRotate: false
  })
  map.value.dragRotate.disable()
  map.value.keyboard.disable()
  map.value.touchZoomRotate.disableRotation()
  map.value.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-left')

  let attributionControl = null
  const setCustomAttribution = () => {
    const attributions = ['©JAXA']
    attributionControl.updateCustomAttributions(attributions)
  }
  attributionControl = new CustomAttributionControl({ compact: true })
  map.value.addControl(attributionControl, 'bottom-left')
  setCustomAttribution()
  map.value.on('zoomend', setCustomAttribution)
  map.value.on('moveend', setCustomAttribution)
  map.value.resize()

  if (props.isLoc) {
    new maplibregl.Marker({ color: '#ac0b00' })
      .setLngLat([props.lon, props.lat])
      .addTo(map.value)
  }

  const [resForecast, resAnlsis] = await Promise.all([fetch(forecastUrl), fetch(anlsisUrl)])
  const forecast = await resForecast.json()
  const anlsis = await resAnlsis.json()
  tiles.value = {
    ...forecast.urls?.tile,
    ...anlsis.urls?.tile
  }
  colorScale.value = forecast.color
  tiles.value = Object.fromEntries(
    Object.entries(tiles.value).filter(([key]) => {
      const hourDiff = dayjs(key).diff(props.now, 'hour')
      return hourDiff >= minHour && hourDiff <= maxHour
    })
  )

  const anlsisKeys = Object.keys(anlsis.urls?.tile ?? {})
  maxAnlsisIndex.value = anlsisKeys.includes(`${props.now.tz('UTC').format('YYYY-MM-DDTHH')}:00:00Z`) ? 0 : -1
  for (let i = minHour; i < maxHour + 1; i += 6) {
    scaleList.value.push({
      index: i,
      tm: props.now.add(i, 'hour'),
      text: i === 0 ? '現在' : i < 0 ? `${i}` : `+${i}`,
      isFuture: i > maxAnlsisIndex.value
    })
  }

  map.value.once('load', async () => {
    setSourceLayer()
    enableScrollZoom()
  })
}

const setSourceLayer = () => {
  const displayDay = props.now.add(indexSlider.value, 'hour')
  Object.entries(tiles.value).forEach(([key, value]) => {
    map.value.addSource(`targetItem_${key}`, {
      type: 'raster',
      tiles: [`${domain}${value}`]
    })
    const opacity = dayjs(key).isSame(displayDay) ? layerOpacity : 0
    const visible = Math.abs(dayjs(key).diff(props.now, 'hour')) < numBeforeReadHour ? 'visible' : 'none'
    map.value.addLayer(
      {
        id: `targetItem_${key}`,
        type: 'raster',
        source: `targetItem_${key}`,
        paint: {
          'raster-resampling': 'nearest',
          'raster-opacity': opacity,
          'raster-opacity-transition': {
            duration: 0,
            delay: 100
          }
        },
        layout: {
          visibility: visible
        }
      }
    )
  })
}

const enableScrollZoom = () => {
  const mouseEnteredHandler = () => {
    mouseEntered.value = true
    mouseEnteredTime.value = Date.now()
  }
  const mouseMoveHandler = () => {
    if (mouseEntered.value) {
      if (Date.now() - mouseEnteredTime.value > 500) {
        map.value.scrollZoom.enable()
        mouseEntered.value = false
      }
    }
  }
  const mouseLeaveHandler = () => {
    mouseEntered.value = false
  }
  const mapElem = map.value.getContainer()
  mapElem.addEventListener('mouseenter', mouseEnteredHandler)
  mapElem.addEventListener('mousemove', mouseMoveHandler)
  mapElem.addEventListener('mouseleave', mouseLeaveHandler)
  mapElem.addEventListener('touchstart', () => {
    map.value.dragPan.enable()
  })
}

const adjust = throttle((param) => {
  indexSlider.value = Number(param)
}, 50)

watch(propsIndexSlider, (newIndex) => {
  adjust(newIndex)
})

watch(indexSlider, (newIndex) => {
  const displayDay = props.now.add(Number(newIndex), 'hour')
  Object.keys(tiles.value).forEach((key) => {
    const opacity = dayjs(key).isSame(displayDay) ? layerOpacity : 0
    const visible = Math.abs(dayjs(key).diff(displayDay, 'hour')) < numBeforeReadHour ? 'visible' : 'none'
    const layer = map.value.getLayer(`targetItem_${key}`)
    if (layer) {
      map.value.setLayoutProperty(layer.id, 'visibility', visible)
      map.value.setPaintProperty(layer.id, 'raster-opacity', opacity)
    } else {
      map.value.addLayer(
        {
          id: `targetItem_${key}`,
          type: 'raster',
          source: `targetItem_${key}`,
          paint: {
            'raster-resampling': 'nearest',
            'raster-opacity': visible ? 1 : 0,
            'raster-opacity-transition': {
              duration: 0,
              delay: 100
            }
          },
          layout: {
            visibility: visible ? 'visible' : 'none'
          }
        }
      )
    }
  })
})

onMounted(async () => {
  nextTick(async () => {
    await initMap()
    isLoaded.value = true
  })
})
</script>

<style lang="scss" scoped>
.map {
  display: flex;
  flex-direction: column;
  gap: 10px;
  .inner {
    padding: 10px 15px;
    background-color: #eef1f8;
  }
}
.main {
  .map {
    @media screen and (min-width: 830px) {
    }
  }
}
.mapContainer {
  min-height: min(480px, calc(100vw - 20px));
}
#mapDisplay {
  width: 100%;
  max-height: 480px;
  aspect-ratio: 1 / 1;
}
.mapLegend {
  position: absolute;
  bottom: 0;
  right: 0;
  z-index: 1;
  pointer-events: none;
  background-color: black;
  opacity: 1;
  padding: 5px;
}
</style>
